import React, { useState } from "react";
import { AirplaneTilt } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";

const TB10_HERO_URL =
  "https://upload.wikimedia.org/wikipedia/commons/7/72/%D0%A1%D0%B0%D0%BC%D0%BE%D0%BB%D0%B5%D1%82_%D0%90%D0%9E%D0%9D_%D0%BD%D0%B0_%D0%BF%D1%83%D1%82%D0%B8_%D0%B2_%D0%A3%D1%84%D1%83.jpg";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("google");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const justReset = typeof window !== "undefined"
    && new URLSearchParams(window.location.search).get("reset") === "1";

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await api.post("/auth/password/login", { email: email.trim().toLowerCase(), password });
      setUser(r.data);
      const adminRole = r.data.role === "owner" || r.data.role === "tenant_admin" || r.data.platform_superuser;
      navigate(adminRole ? "/dashboard" : "/calendar");
    } catch (e) {
      setErr(e.response?.data?.detail || "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#0A0A0A] text-zinc-100">
      <div className="absolute inset-0" style={{ backgroundImage: `url("${TB10_HERO_URL}")`, backgroundSize: "cover", backgroundPosition: "center 35%" }} />
      <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(10,10,10,0.45) 0%, rgba(10,10,10,0.15) 30%, rgba(122,67,25,0.18) 60%, rgba(10,10,10,0.55) 90%, rgba(10,10,10,0.85) 100%)" }} />
      <div className="absolute inset-0 hidden lg:block bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
      <div className="absolute inset-0 lg:hidden bg-gradient-to-b from-[#0A0A0A]/60 via-[#0A0A0A]/40 to-[#0A0A0A]" />

      <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
        <div className="flex items-center justify-center p-8 lg:p-16">
          <div className="max-w-md w-full">
            <div className="flex items-center gap-3 mb-12">
              <div className="w-9 h-9 border border-amber flex items-center justify-center">
                <AirplaneTilt size={20} weight="bold" className="text-amber" />
              </div>
              <div className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400">OwnerCAM</div>
            </div>

            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Sign in</div>
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-4">
              Welcome back<br /> to <span className="amber">OwnerCAM</span>.
            </h1>
            <p className="text-sm text-zinc-400 leading-relaxed mb-10">
              Sign in to keep tracking your aircraft maintenance, inspections,
              renewals, parts life, ADs, documents — and cost sharing on the Pro
              plan.
            </p>

            {mode === "google" ? (
              <>
                <button
                  data-testid="google-login-button"
                  onClick={handleGoogle}
                  className="w-full h-12 bg-amber text-black font-medium tracking-wide uppercase text-sm hover:bg-[#FFC033] transition-colors duration-150 border border-amber"
                >
                  Sign in with Google
                </button>
                <div className="flex items-center gap-3 my-4">
                  <div className="h-px flex-1 bg-zinc-800" />
                  <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 font-mono">or</span>
                  <div className="h-px flex-1 bg-zinc-800" />
                </div>
                <button
                  data-testid="show-password-login-btn"
                  onClick={() => setMode("password")}
                  className="w-full h-11 bg-transparent text-zinc-300 font-medium tracking-wide uppercase text-xs hover:text-amber transition-colors border border-zinc-700 hover:border-amber/50"
                >
                  Sign in with email and password
                </button>
                <div className="text-center text-xs text-zinc-500 mt-6 pt-4 border-t border-zinc-900">
                  No workspace yet?{" "}
                  <Link to="/signup" className="amber hover:underline" data-testid="login-to-signup-google">
                    Create one in 30 seconds
                  </Link>
                </div>
              </>
            ) : (
              <form onSubmit={handlePasswordLogin} className="space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Email</label>
                  <input
                    data-testid="login-email"
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email" required
                    className="w-full h-11 bg-[#0A0A0A] border border-zinc-700 focus:border-amber px-3 text-sm mt-1 outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Password</label>
                  <input
                    data-testid="login-password"
                    type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password" required
                    className="w-full h-11 bg-[#0A0A0A] border border-zinc-700 focus:border-amber px-3 text-sm mt-1 outline-none"
                  />
                </div>
                {err && <div className="text-xs text-red-400 font-mono" data-testid="login-error">{err}</div>}
                <button
                  type="submit" disabled={busy}
                  data-testid="password-login-submit"
                  className="w-full h-12 bg-amber text-black font-medium tracking-wide uppercase text-sm hover:bg-[#FFC033] transition-colors border border-amber disabled:opacity-50"
                >
                  {busy ? "Signing in…" : "Sign in"}
                </button>
                <button
                  type="button" onClick={() => { setMode("google"); setErr(""); }}
                  className="w-full text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-amber font-mono pt-2"
                  data-testid="show-google-login-btn"
                >
                  ← Use Google instead
                </button>
                <div className="text-center text-xs text-zinc-500 pt-2 border-t border-zinc-900 mt-4">
            New here?{" "}
            <Link to="/signup" className="amber hover:underline" data-testid="login-to-signup">
              Create a workspace
            </Link>
          </div>
        </form>
            )}

            <div className="mt-8 grid grid-cols-3 gap-4 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
              <div className="border-t border-zinc-800 pt-3">No public<br/>registration</div>
              <div className="border-t border-zinc-800 pt-3">Owner approval<br/>required</div>
              <div className="border-t border-zinc-800 pt-3">Cost-sharing<br/>only</div>
            </div>
          </div>
        </div>

        <div className="hidden lg:flex items-end p-16 relative">
          <div className="font-mono text-[10px] text-zinc-300 leading-relaxed border-l border-amber/60 pl-4 max-w-xs bg-[#0A0A0A]/70 py-3 backdrop-blur-sm">
            <div className="text-amber/80 mb-2">// SYSTEM POSITIONING</div>
            This system is a private coordination tool. It is not a rental platform,
            booking marketplace, or payment system. All interactions reflect shared
            operating costs only.
          </div>
        </div>
      </div>
    </div>
  );
}
